import { cloneDeep, set } from "lodash";
import React, { FC, HTMLAttributes, useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { useDispatch } from "react-redux";
import ApiStateModal from "../../../components/reusable-ui/basic-modal";
import { MESSAGE_TYPE, COMPONENT_MODES } from "../../../data/constants";
import { isAddNewMode, isEditMode } from "../../../helpers/catalog-helper";
import { getAllBrandsData, saveBrandData } from "../../../redux/slices";
import { RootState } from "../../../redux/store";
import BrandsDetailsForm from "./brand-details";
import BrandsViewComponent from "./brands-view";
import "./style.scss";

const BrandsManagement: FC<HTMLAttributes<HTMLDivElement>> = () => {
  const [brandId, setBrandId] = useState<string>("");
  const [isChangesMade, setIsChangesMade] = useState<boolean>(false);
  const [errorMessageMap, setErrorMsgMap] = useState<any>({});
  const [brandDetails, setBrandDetails] = useState<any>({});
  const [modeConfig, setModeConfig] = useState<any>({
    //it will determine whether it's in view mode or edit mode
    type: COMPONENT_MODES.viewMode,
    tabId: "",
  });
  const brandsDataMap = useSelector(
    (state: RootState) => state.brandsReducer.brandsData
  );
  const [brandNamesList, setBrandNamesList] = useState<any>([]);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [successModalText, setSuccessModalText] = useState("");
  const dispatch = useDispatch();

  useEffect(() => {
    dispatch(getAllBrandsData());
  }, []);

  useEffect(() => {
    if (brandsDataMap && Object.keys(brandsDataMap)?.length) {
      let dataToUpdate: any = brandsDataMap
        ? Object.values(brandsDataMap).map((brandData: any, index: number) => {
            if (brandId !== brandData?.mid) {
              return brandData?.name;
            }
          })
        : [];
      setBrandNamesList(dataToUpdate);
    }
  }, [brandsDataMap, brandId]);

  useEffect(() => {
    if (isAddNewMode(modeConfig.type) || !brandId) {
      setBrandDetails({});
      setErrorMsgMap({});
    }
  }, [modeConfig, brandId]);

  useEffect(() => {
    if (!isAddNewMode(modeConfig?.type) && brandId) {
      setBrandDetails(brandsDataMap[brandId]);
    }
  }, [brandId, brandsDataMap]);

  const updateCurDataInParentState = (key: string, value: any) => {
    const dataToUpdate = cloneDeep(brandDetails) || {};
    set(dataToUpdate, key, value);
    setBrandDetails(dataToUpdate);
    setIsChangesMade(true);
  };

  const onDiscardCtaClick = () => {
    resetStateOnClose();
    if (!isAddNewMode(modeConfig?.type)) {
      if (brandId) {
        setBrandDetails(cloneDeep(brandsDataMap[brandId]));
      } else {
        setBrandDetails({});
      }
    }
  };

  const validateBrandDetails = (brandDetails: any) => {
    if (
      brandDetails?.name &&
      brandNamesList.indexOf(brandDetails?.name) === -1
    ) {
      return { isValid: true, dataToPost: brandDetails };
    }
    let errorMsgMap: any = {};

    if (!brandDetails?.name) {
      errorMsgMap.name = "This is mandatory field";
    }

    if (
      brandDetails?.name &&
      brandNamesList.indexOf(brandDetails?.name) !== -1
    ) {
      errorMsgMap.name = "Name should be unique";
    }

    return { isValid: false, errorMsgMap };
  };

  const onSaveCtaClick = () => {
    const validatedData: any = validateBrandDetails(brandDetails);
    const callback = (response: any) => {
      if (isEditMode(modeConfig?.type)) {
        // dispatch(updateChargesList({ key: brandId, data: response }));
        setSuccessModalText("Changes Saved Successfully");
      } else if (isAddNewMode(modeConfig?.type)) {
        // dispatch(getAllBrandsData());
        setSuccessModalText("Brand Created Successfully");
      }
      dispatch(getAllBrandsData());

      setShowSuccessModal(true);
      resetStateOnClose();
    };
    if (validatedData?.isValid) {
      dispatch(
        saveBrandData({
          dataToPost: brandDetails,
          callback,
        })
      );
    } else {
      setErrorMsgMap(validatedData?.errorMsgMap);
    }
  };

  function resetStateOnClose() {
    setErrorMsgMap({});
    setIsChangesMade(false);

    setModeConfig({ type: COMPONENT_MODES.viewMode });
  }

  function clearCurData() {
    setBrandDetails({});
  }

  const commonProps = {
    curData: brandDetails,
    modeConfig,
    setModeConfig,
    updateCurDataInParentState,
    curDataId: brandId,
    errorMessageMap,
    onSaveCtaClick,
    onDiscardCtaClick,
    isChangesMade,
    clearCurData,
  };

  return (
    <section className="flex h-[calc(100vh-141px)] overflow-hidden sm:flex-col brandsManageContainer">
      <BrandsViewComponent openDetailsView={setBrandId} {...commonProps} />

      {brandId ? <BrandsDetailsForm {...commonProps} /> : null}

      {showSuccessModal && (
        <ApiStateModal
          modalType={MESSAGE_TYPE.success}
          open={showSuccessModal}
          handleClose={() => setShowSuccessModal(false)}
          mainText={successModalText}
        ></ApiStateModal>
      )}
    </section>
  );
};

export default BrandsManagement;
